import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

import PerfectScrollbar from "perfect-scrollbar";
import {onBeforeUnmount, onMounted, reactive, ref, watch} from "vue";
import CasesCompleted from "@/components/modules/Analytics/AnalyticsByParam/CasesCompleted.vue";
import CasesSentToCourt from "@/components/modules/Analytics/AnalyticsByParam/CasesSentToCourt.vue";
import CasesSuspended from "@/components/modules/Analytics/AnalyticsByParam/CasesSuspended.vue";
import CasesReturned from "@/components/modules/Analytics/AnalyticsByParam/CasesReturned.vue";
import CasesAccepted from "@/components/modules/Analytics/AnalyticsByParam/CasesAccepted.vue";
import AccusedInCourt from "@/components/modules/Analytics/AnalyticsByParam/AccusedInCourt.vue";
import PersonsRehabilitated from "@/components/modules/Analytics/AnalyticsByParam/PersonsRehabilitated.vue";
import {District, Month, Region} from "@/types/files";
import {AnalyticsState, LocationType} from "@/types/analytics";
import {MapDisplayParamsKey} from "@/composables/constants/params";
import {useAnalyticStore} from "@/stores/analytics";
import {DynamicQuery, RegionTopQuery, TotalMetricDataQuery} from "@/types/analytics/requests";

import type {
  CasesAcceptedAnalytics,
  CasesSentToCourtAnalytics,
  CasesSuspendedAnalytics,
  ReturnedCasesAnalytics,
  OvertimeCasesAnalytics,
  AccusedPersonsAnalytics,
  RehabilitatedPersonsAnalytics
} from "@/types/analytics";

interface MapAnalyticsProps {
  paramName: MapDisplayParamsKey;
  selectedMonth: Month;
  selectedYear: number;
  selectedRegion: Region | null;
  selectedDistrict: District | null;
}

// TODO: Передать на бек, что бы привели все к единообразию, затем вынести в loadTotalData и loadTopRegions. 19.12.2025.
// TODO: regions => region. federal_district => district, federal_districts => district, districts => district.
// Активный тип аналитики. (Россия, Округа, Регионы).

export default /*@__PURE__*/_defineComponent({
  __name: 'MapAnalytics',
  props: {
    paramName: {},
    selectedMonth: {},
    selectedYear: {},
    selectedRegion: {},
    selectedDistrict: {}
  },
  setup(__props: any) {

const activeType = ref<LocationType>("russia");
// Установить активный тип аналитики
const setAnalyticsActiveType = () => {
  // Определяем активный тип аналитики в зависимости от выбранного округа/региона
  if (props.selectedDistrict === null && props.selectedRegion === null) {
    // 1) Запрос по России
    activeType.value = "russia";
  } else if (props.selectedDistrict !== null && props.selectedRegion === null) {
    // 2) Запрос по округу
    activeType.value = "district";
  } else {
    // 3) Запрос по региону
    activeType.value = "region";
  }
};
/////////////////////

const props = __props;

const analyticStore = useAnalyticStore();

// Perfect Scrollbar
const scrollContainer = ref<HTMLElement | null>(null);
let ps: PerfectScrollbar | null = null;

onMounted(async () => {
  await loadAnalyticsData();
  updateScroll();
});

onBeforeUnmount(() => {
  if (ps) {
    ps.destroy();
    ps = null;
  }
});

// Обновление скролла
const updateScroll = () => {
  if (scrollContainer.value) {
    ps = new PerfectScrollbar(scrollContainer.value, {
      wheelSpeed: 1,
      wheelPropagation: false,
      minScrollbarLength: 20
    });
  }
};

// Проверяем наличие изменения выбранного параметра, округа или региона для обновления данных.
watch(
  () => [props.paramName, props.selectedDistrict, props.selectedRegion, props.selectedYear, props.selectedMonth],
  async () => {
    await loadAnalyticsData();
    updateScroll();
  }
);

const loadAnalyticsData = async () => {
  setAnalyticsActiveType();
  await Promise.all([
    loadTotalData(),
    loadTopRegions(),
    loadDynamic()
  ]);
};

// Загрузка статичн
const loadTotalData = async () => {
  const baseParams = {
    year: props.selectedYear,
    month: props.selectedMonth,
  };

  let queryParams: TotalMetricDataQuery;

  // Определяем тип запроса и параметры в зависимости от выбранного округа/региона
  if (props.selectedDistrict === null && props.selectedRegion === null) {
    // 1) Запрос по России
    queryParams = { ...baseParams, type: "russia" };
  } else if (props.selectedDistrict !== null && props.selectedRegion === null) {
    // 2) Запрос по округу
    queryParams = { ...baseParams, type: "federal_district", district: props.selectedDistrict };
  } else {
    // 3) Запрос по региону
    queryParams = { ...baseParams, type: "region", region: props.selectedRegion ?? undefined };
  }

  const response = await analyticStore.getAnalyticMetricData(queryParams, props.paramName);

  Object.assign(analyticsData, {
    totalData: response
  });
};



// Загрузка динамики данных помесячно
const loadDynamic = async () => {
  let type: "russia" | "districts" | "regions";
  const queryParams: DynamicQuery = {
    metricName: props.paramName,
    year: props.selectedYear
  };

  // Определяем тип запроса и параметры в зависимости от выбранного округа/региона
  if (props.selectedDistrict === null && props.selectedRegion === null) {
    // 1) Запрос по России
    type = "russia";
  } else if (props.selectedDistrict !== null && props.selectedRegion === null) {
    // 2) Запрос по округу
    type = "districts";
    queryParams.district = props.selectedDistrict;
  } else {
    // 3) Запрос по региону
    type = "regions";
    queryParams.region = props.selectedRegion ?? undefined;
  }

  const response = await analyticStore.getDynamicByMonth(queryParams, type);

  Object.assign(analyticsData, {
    monthlyDynamic: response
  });
};

// Загрузка ТОП-10 регионов с наибольшими/наименьшими показателями.
const loadTopRegions = async () => {
  const baseQueryParams: RegionTopQuery = {
    metricName: props.paramName,
    year: props.selectedYear,
    month: props.selectedMonth,
    count: 10
  };

  const [topAscending, topDescending] = await Promise.all([
    analyticStore.getTopRegions({ ...baseQueryParams, sortOrder: "ascending" }),
    analyticStore.getTopRegions({ ...baseQueryParams, sortOrder: "descending" })
  ]);

  Object.assign(analyticsData, { topAscending, topDescending });
};

const analyticsData: AnalyticsState = reactive({
  topAscending: null,
  topDescending: null,
  monthlyDynamic: null,
  totalData: null
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "scrollContainer",
    ref: scrollContainer,
    class: "analytics"
  }, [
    (__props.paramName === 'cases_accepted')
      ? (_openBlock(), _createBlock(CasesAccepted, {
          key: 0,
          "active-type": activeType.value,
          "selected-region": __props.selectedRegion,
          "selected-district": __props.selectedDistrict,
          "analytic-data": analyticsData as CasesAcceptedAnalytics
        }, null, 8, ["active-type", "selected-region", "selected-district", "analytic-data"]))
      : (__props.paramName === 'cases_sent_to_court_with_indictment')
        ? (_openBlock(), _createBlock(CasesSentToCourt, {
            key: 1,
            "active-type": activeType.value,
            "selected-region": __props.selectedRegion,
            "selected-district": __props.selectedDistrict,
            "analytic-data": analyticsData as CasesSentToCourtAnalytics
          }, null, 8, ["active-type", "selected-region", "selected-district", "analytic-data"]))
        : (__props.paramName === 'cases_suspended')
          ? (_openBlock(), _createBlock(CasesSuspended, {
              key: 2,
              "active-type": activeType.value,
              "selected-region": __props.selectedRegion,
              "selected-district": __props.selectedDistrict,
              "analytic-data": analyticsData as CasesSuspendedAnalytics
            }, null, 8, ["active-type", "selected-region", "selected-district", "analytic-data"]))
          : (__props.paramName === 'cases_returned_for_investigation')
            ? (_openBlock(), _createBlock(CasesReturned, {
                key: 3,
                "active-type": activeType.value,
                "selected-region": __props.selectedRegion,
                "selected-district": __props.selectedDistrict,
                "analytic-data": analyticsData as ReturnedCasesAnalytics
              }, null, 8, ["active-type", "selected-region", "selected-district", "analytic-data"]))
            : (__props.paramName === 'cases_completed_overtime')
              ? (_openBlock(), _createBlock(CasesCompleted, {
                  key: 4,
                  "active-type": activeType.value,
                  "selected-region": __props.selectedRegion,
                  "selected-district": __props.selectedDistrict,
                  "analytic-data": analyticsData as OvertimeCasesAnalytics
                }, null, 8, ["active-type", "selected-region", "selected-district", "analytic-data"]))
              : (__props.paramName === 'accused_in_court_cases')
                ? (_openBlock(), _createBlock(AccusedInCourt, {
                    key: 5,
                    "active-type": activeType.value,
                    "selected-region": __props.selectedRegion,
                    "selected-district": __props.selectedDistrict,
                    "analytic-data": analyticsData as AccusedPersonsAnalytics
                  }, null, 8, ["active-type", "selected-region", "selected-district", "analytic-data"]))
                : (__props.paramName === 'persons_rehabilitated')
                  ? (_openBlock(), _createBlock(PersonsRehabilitated, {
                      key: 6,
                      "active-type": activeType.value,
                      "selected-region": __props.selectedRegion,
                      "selected-district": __props.selectedDistrict,
                      "analytic-data": analyticsData as RehabilitatedPersonsAnalytics
                    }, null, 8, ["active-type", "selected-region", "selected-district", "analytic-data"]))
                  : _createCommentVNode("", true)
  ], 512))
}
}

})